import React, { useCallback, useEffect, useState } from 'react';
import { showToast } from '../../../utils/showToast';
import {
  getConfig, guardarConfig, pingContifico, listarConceptos, actualizarConcepto,
  listarProductosContifico, toggleProductoContifico, syncCatalogo, listarCuentasBancarias,
  listarCuentasBancariasCatalogo, toggleCuentaBancaria, syncCuentasBancarias,
  listarBodegasContifico,
} from '../../../services/contificoService';
import { listarBodegasInventario, actualizarBodegaContifico } from '../../../services/inventarioService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EstadoBadge = ({ ok, cargando }) => {
  if (cargando) return <span className="badge bg-secondary"><i className="bi bi-hourglass-split me-1" />Verificando...</span>;
  if (ok === true)  return <span className="badge bg-success"><i className="bi bi-check-circle-fill me-1" />Conectado</span>;
  if (ok === false) return <span className="badge bg-danger"><i className="bi bi-x-circle-fill me-1" />Sin conexión</span>;
  return <span className="badge bg-secondary"><i className="bi bi-dash-circle me-1" />No verificado</span>;
};

// ── Componente principal ──────────────────────────────────────────────────────

// Las credenciales (API Key/Token) solo las puede ver o editar el superadmin —
// el resto de la pantalla (facturación automática, jobs, catálogos, etc.) es de
// cualquiera con el permiso contifico.configurar. El backend refuerza lo mismo
// en guardarConfig() sin importar lo que llegue desde aquí.
const esSuperadmin = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.rol_id === 1;
  } catch {
    return false;
  }
};

export default function ConfigContificoView() {
  const isRoot = esSuperadmin();
  const [config, setConfig]           = useState(null);
  const [cargandoConfig, setCargandoConfig] = useState(true);

  const [apiKey,           setApiKey]           = useState('');
  const [apiToken,         setApiToken]         = useState('');
  const [facAuto,          setFacAuto]          = useState(false);
  const [habilitado,       setHabilitado]       = useState(false);
  const [establecimiento,  setEstablecimiento]  = useState('');
  const [emision,          setEmision]          = useState('');
  const [proximoNumero,    setProximoNumero]    = useState('');
  const [cuentaBancariaId, setCuentaBancariaId] = useState('');

  // Jobs automáticos
  const [reintentoHabilitado,    setReintentoHabilitado]    = useState(true);
  const [reintentoIntervalo,     setReintentoIntervalo]     = useState(15);
  const [reintentoMaxIntentos,   setReintentoMaxIntentos]   = useState(3);
  const [reintentoEdadMin,       setReintentoEdadMin]       = useState(5);
  const [pollingHabilitado,      setPollingHabilitado]      = useState(true);
  const [pollingIntervalo,       setPollingIntervalo]       = useState(10);
  const [pollingAlertaHoras,     setPollingAlertaHoras]     = useState(24);

  const [cuentasBancarias,       setCuentasBancarias]       = useState([]);
  const [cargandoCuentas,        setCargandoCuentas]        = useState(false);

  // Catálogo de cuentas bancarias — mismo patrón que el catálogo de productos
  const [cuentasCatalogo,   setCuentasCatalogo]   = useState([]);
  const [syncandoCuentas,   setSyncandoCuentas]   = useState(false);
  const [togglingCuenta,    setTogglingCuenta]    = useState({});  // { [id]: bool }

  const [mostrarKey,   setMostrarKey]   = useState(false);
  const [mostrarToken, setMostrarToken] = useState(false);

  const [estadoPing,    setEstadoPing]    = useState(null);   // true | false | null
  const [cargandoPing,  setCargandoPing]  = useState(false);
  const [mensajePing,   setMensajePing]   = useState('');

  const [guardando, setGuardando] = useState(false);
  const [dirty,     setDirty]     = useState(false);   // cambios sin guardar en el formulario de configuración
  const marcarDirty = () => setDirty(true);

  // Catálogo de productos
  const [productos,        setProductos]        = useState([]);
  const [syncandoCatalogo, setSyncandoCatalogo] = useState(false);
  const [toggling,         setToggling]         = useState({});  // { [id]: bool }

  // Conceptos de pago
  const [conceptos,       setConceptos]       = useState([]);
  const [codigosEdit,     setCodigosEdit]     = useState({});   // { [id]: string }
  const [guardandoConcep, setGuardandoConcep] = useState({});   // { [id]: bool }

  // Bodegas — enlace manual con Contifico (evita que el sync adivine por
  // nombre exacto y termine creando una bodega duplicada alla).
  const [bodegasLocales,    setBodegasLocales]    = useState([]);
  const [bodegasContifico,  setBodegasContifico]  = useState([]);
  const [bodegasEdit,       setBodegasEdit]       = useState({}); // { [id]: contifico_bodega_id }
  const [guardandoBodega,   setGuardandoBodega]   = useState({}); // { [id]: bool }
  const [cargandoBodegasContifico, setCargandoBodegasContifico] = useState(false);

  // Cargar config actual
  const cargarConfig = useCallback(async () => {
    setCargandoConfig(true);
    try {
      const data = await getConfig();
      setConfig(data);
      setFacAuto(data.facturacion_automatica);
      setHabilitado(data.habilitado);
      const [est = '', emi = ''] = (data.serie_factura || '').split('-');
      setEstablecimiento(est);
      setEmision(emi);
      setProximoNumero(data.proximo_numero_factura || '');
      setCuentaBancariaId(data.cuenta_bancaria_id || '');
      // Jobs
      setReintentoHabilitado(data.reintento_habilitado  ?? true);
      setReintentoIntervalo(data.reintento_intervalo_min ?? 15);
      setReintentoMaxIntentos(data.reintento_max_intentos ?? 3);
      setReintentoEdadMin(data.reintento_edad_min       ?? 5);
      setPollingHabilitado(data.polling_habilitado      ?? true);
      setPollingIntervalo(data.polling_intervalo_min    ?? 10);
      setPollingAlertaHoras(data.polling_alerta_horas   ?? 24);
    } catch {
      showToast({ type: 'danger', message: 'No se pudo cargar la configuración de Contífico.' });
    } finally {
      setCargandoConfig(false);
    }
  }, []);

  useEffect(() => { cargarConfig(); }, [cargarConfig]);

  // Ping automático en segundo plano — no bloquea la carga de la página
  useEffect(() => {
    if (!config) return;
    if (!config.api_key_guardada || !config.api_token_guardada) return;
    setCargandoPing(true);
    pingContifico()
      .then(ping => { setEstadoPing(true); setMensajePing(ping.mensaje || 'Conexión establecida.'); })
      .catch(() => { setEstadoPing(false); setMensajePing('No se pudo conectar con Contífico.'); })
      .finally(() => setCargandoPing(false));
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarProductos = useCallback(async () => {
    try {
      const data = await listarProductosContifico();
      setProductos(data || []);
    } catch { /* sin catálogo aún */ }
  }, []);

  useEffect(() => { cargarProductos(); }, [cargarProductos]);

  const cargarCuentasBancarias = useCallback(async () => {
    setCargandoCuentas(true);
    try {
      const data = await listarCuentasBancarias();
      setCuentasBancarias(data || []);
    } catch { /* sin cuentas o sin conexión */ }
    finally { setCargandoCuentas(false); }
  }, []);

  useEffect(() => { cargarCuentasBancarias(); }, [cargarCuentasBancarias]);

  const cargarCuentasCatalogo = useCallback(async () => {
    try {
      const data = await listarCuentasBancariasCatalogo();
      setCuentasCatalogo(data || []);
    } catch { /* sin catálogo aún */ }
  }, []);

  useEffect(() => { cargarCuentasCatalogo(); }, [cargarCuentasCatalogo]);

  const handleToggleCuenta = async (c) => {
    setTogglingCuenta(prev => ({ ...prev, [c.id]: true }));
    try {
      await toggleCuentaBancaria(c.id, !c.activo);
      setCuentasCatalogo(prev => prev.map(x => x.id === c.id ? { ...x, activo: !c.activo } : x));
      cargarCuentasBancarias(); // refresca el select de "cuenta por defecto", que solo lista activas
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'Error al actualizar.' });
    } finally {
      setTogglingCuenta(prev => ({ ...prev, [c.id]: false }));
    }
  };

  const handleSyncCuentas = async () => {
    setSyncandoCuentas(true);
    try {
      const res = await syncCuentasBancarias();
      showToast({ type: 'success', message: res.message });
      cargarCuentasCatalogo();
      cargarCuentasBancarias();
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'Error al sincronizar cuentas bancarias.' });
    } finally {
      setSyncandoCuentas(false);
    }
  };

  const handleToggleProducto = async (p) => {
    setToggling(prev => ({ ...prev, [p.id]: true }));
    try {
      await toggleProductoContifico(p.id, !p.activo);
      setProductos(prev => prev.map(x => x.id === p.id ? { ...x, activo: !p.activo } : x));
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'Error al actualizar.' });
    } finally {
      setToggling(prev => ({ ...prev, [p.id]: false }));
    }
  };

  const handleSyncCatalogo = async () => {
    setSyncandoCatalogo(true);
    try {
      const res = await syncCatalogo();
      showToast({ type: 'success', message: res.message });
      cargarProductos();
      cargarConceptos();
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'Error al sincronizar catálogo.' });
    } finally {
      setSyncandoCatalogo(false);
    }
  };

  const cargarBodegasLocales = useCallback(async () => {
    try {
      const data = await listarBodegasInventario();
      setBodegasLocales(data || []);
      const edits = {};
      (data || []).forEach(b => { edits[b.id] = b.contifico_bodega_id || ''; });
      setBodegasEdit(edits);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { cargarBodegasLocales(); }, [cargarBodegasLocales]);

  // Las bodegas reales de Contifico se piden en vivo (no hay cache local,
  // son pocas) — solo una vez, la primera vez que hay bodegas locales que
  // enlazar.
  const cargarBodegasContifico = useCallback(async () => {
    setCargandoBodegasContifico(true);
    try {
      const data = await listarBodegasContifico();
      setBodegasContifico(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'No se pudieron cargar las bodegas de Contífico.' });
    } finally {
      setCargandoBodegasContifico(false);
    }
  }, []);

  useEffect(() => {
    if (bodegasLocales.length) cargarBodegasContifico();
  }, [bodegasLocales.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGuardarBodega = async (bodega) => {
    setGuardandoBodega(prev => ({ ...prev, [bodega.id]: true }));
    try {
      await actualizarBodegaContifico(bodega.id, bodegasEdit[bodega.id] || null);
      setBodegasLocales(prev => prev.map(b =>
        b.id === bodega.id ? { ...b, contifico_bodega_id: bodegasEdit[bodega.id] || null } : b
      ));
      showToast({ type: 'success', message: 'Bodega enlazada correctamente.' });
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'Error al enlazar la bodega.' });
    } finally {
      setGuardandoBodega(prev => ({ ...prev, [bodega.id]: false }));
    }
  };

  const cargarConceptos = useCallback(async () => {
    try {
      const data = await listarConceptos();
      setConceptos(data || []);
      const edits = {};
      (data || []).forEach(c => { edits[c.id] = c.contifico_codigo || ''; });
      setCodigosEdit(edits);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { cargarConceptos(); }, [cargarConceptos]);

  const handleGuardarConcepto = async (concepto) => {
    setGuardandoConcep(prev => ({ ...prev, [concepto.id]: true }));
    try {
      const res = await actualizarConcepto(concepto.id, { contifico_codigo: codigosEdit[concepto.id] || '' });
      setConceptos(prev => prev.map(c =>
        c.id === concepto.id
          ? { ...c, contifico_codigo: res.contifico_codigo, contifico_producto_id: res.contifico_producto_id }
          : c
      ));
      showToast({ type: 'success', message: res.message });
    } catch (err) {
      showToast({ type: 'danger', message: err?.message || 'Error al guardar el mapeo.' });
    } finally {
      setGuardandoConcep(prev => ({ ...prev, [concepto.id]: false }));
    }
  };

  // Probar conexión con las credenciales del formulario (si las escribió) o con las guardadas
  const handlePing = async () => {
    setCargandoPing(true);
    setEstadoPing(null);
    setMensajePing('');
    try {
      // Si escribió credenciales en el form, las usa para probar; si no, usa las guardadas en BD
      const resultado = await pingContifico(apiKey || undefined, apiToken || undefined);
      setEstadoPing(true);
      setMensajePing(resultado.mensaje || 'Conexión establecida.');
    } catch (err) {
      setEstadoPing(false);
      setMensajePing(err?.response?.message || err?.message || 'No se pudo conectar con Contífico.');
    } finally {
      setCargandoPing(false);
    }
  };

  // Guardar configuración
  const handleGuardar = async () => {
    // Si ya había credenciales guardadas y el usuario escribió algo en Key/Token,
    // confirmar explícitamente: esto reemplaza lo que configuró el superadmin.
    const sobrescribeKey   = apiKey.trim()   && config?.api_key_guardada;
    const sobrescribeToken = apiToken.trim() && config?.api_token_guardada;
    if (sobrescribeKey || sobrescribeToken) {
      const confirmado = window.confirm(
        'Ya existen credenciales de Contífico guardadas.\n\n' +
        'Vas a REEMPLAZARLAS con lo que escribiste en el formulario. Si no estás seguro de estos valores, ' +
        'cancela, usa "Probar conexión" para verificarlos primero y guarda solo cuando confirmes que son correctos.\n\n' +
        '¿Confirmas que quieres sobrescribir las credenciales guardadas?'
      );
      if (!confirmado) return;
    }

    setGuardando(true);
    try {
      const serie = (establecimiento && emision)
        ? `${establecimiento.trim()}-${emision.trim()}`
        : '';
      const payload = {
        habilitado,
        facturacion_automatica: facAuto,
        serie_factura: serie,
        proximo_numero_factura: proximoNumero.trim() || '',
        cuenta_bancaria_id: cuentaBancariaId.trim() || '',
        ...(apiKey   ? { api_key:   apiKey }   : {}),
        ...(apiToken ? { api_token: apiToken } : {}),
        // Jobs
        reintento_habilitado:    reintentoHabilitado,
        reintento_intervalo_min: Number(reintentoIntervalo)   || 15,
        reintento_max_intentos:  Number(reintentoMaxIntentos) || 3,
        reintento_edad_min:      Number(reintentoEdadMin)     || 5,
        polling_habilitado:      pollingHabilitado,
        polling_intervalo_min:   Number(pollingIntervalo)     || 10,
        polling_alerta_horas:    Number(pollingAlertaHoras)   || 24,
      };
      const data = await guardarConfig(payload);
      setConfig(data);
      setApiKey('');
      setApiToken('');
      setDirty(false);
      showToast({ type: 'success', message: 'Configuración guardada correctamente.' });
    } catch {
      showToast({ type: 'danger', message: 'Error al guardar la configuración.' });
    } finally {
      setGuardando(false);
    }
  };

  if (cargandoConfig) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 760 }}>

      {/* Encabezado */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="bg-primary bg-opacity-10 rounded-3 p-3">
          <i className="bi bi-plug-fill fs-3 text-primary" />
        </div>
        <div>
          <h4 className="mb-0 fw-bold">Configuración Contífico</h4>
          <small className="text-muted">Facturación electrónica y contabilidad — API v2</small>
        </div>
        <div className="ms-auto">
          <EstadoBadge ok={estadoPing} cargando={cargandoPing} />
        </div>
      </div>

      {/* Card estado de conexión */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <p className="mb-1 fw-semibold">Estado de conexión</p>
              <small className="text-muted">
                {config?.api_key_guardada && config?.api_token_guardada
                  ? <>Credenciales guardadas — Key: <code>{config.api_key_preview}</code> · Token: <code>{config.api_token_preview}</code></>
                  : 'No hay credenciales guardadas todavía.'}
              </small>
              {mensajePing && (
                <div className={`alert py-2 px-3 mt-2 mb-0 alert-${estadoPing ? 'success' : 'danger'}`} style={{ fontSize: 13 }}>
                  {mensajePing}
                </div>
              )}
            </div>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handlePing}
              disabled={cargandoPing}
            >
              {cargandoPing
                ? <><span className="spinner-border spinner-border-sm me-1" />Verificando...</>
                : <><i className="bi bi-wifi me-1" />Probar conexión</>}
            </button>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div>
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-bottom py-3">
            <h6 className="mb-0 fw-semibold">Credenciales API</h6>
          </div>
          <div className="card-body">
            {isRoot ? (
              <>
                <p className="text-muted small mb-4">
                  <i className="bi bi-shield-lock me-1" />
                  Deja el campo en blanco si no quieres cambiar la credencial guardada. Las credenciales se almacenan cifradas.
                </p>

                {/* API Key */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    API Key
                    {config?.api_key_guardada && (
                      <span className="badge bg-success ms-2" style={{ fontSize: 11 }}>guardada</span>
                    )}
                  </label>
                  <div className="input-group">
                    <input
                      type={mostrarKey ? 'text' : 'password'}
                      className="form-control"
                      placeholder={config?.api_key_guardada ? config.api_key_preview : 'Ingresa tu API Key de Contífico'}
                      value={apiKey}
                      onChange={e => { setApiKey(e.target.value); marcarDirty(); }}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setMostrarKey(v => !v)}
                      tabIndex={-1}
                    >
                      <i className={`bi bi-eye${mostrarKey ? '-slash' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* API Token */}
                <div className="mb-0">
                  <label className="form-label fw-semibold">
                    API Token
                    {config?.api_token_guardada && (
                      <span className="badge bg-success ms-2" style={{ fontSize: 11 }}>guardada</span>
                    )}
                  </label>
                  <div className="input-group">
                    <input
                      type={mostrarToken ? 'text' : 'password'}
                      className="form-control"
                      placeholder={config?.api_token_guardada ? config.api_token_preview : 'Ingresa tu API Token de Contífico'}
                      value={apiToken}
                      onChange={e => { setApiToken(e.target.value); marcarDirty(); }}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setMostrarToken(v => !v)}
                      tabIndex={-1}
                    >
                      <i className={`bi bi-eye${mostrarToken ? '-slash' : ''}`} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="alert alert-secondary mb-0 d-flex gap-2" style={{ fontSize: 13 }}>
                <i className="bi bi-lock-fill mt-1" />
                <div>
                  <strong>Solo el superadministrador puede ver o modificar la API Key y el API Token.</strong>
                  <div className="text-muted mt-1">
                    {config?.api_key_guardada && config?.api_token_guardada
                      ? 'La conexión ya está configurada. El resto de opciones de esta pantalla (facturación automática, jobs, catálogos, conceptos) sí puedes editarlas y guardarlas normalmente.'
                      : 'Todavía no hay credenciales guardadas — pide al superadministrador que las configure.'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Opciones */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-bottom py-3">
            <h6 className="mb-0 fw-semibold">Opciones</h6>
          </div>
          <div className="card-body">

            {/* Habilitar módulo */}
            <div className="d-flex align-items-center justify-content-between py-2 border-bottom mb-3">
              <div>
                <p className="mb-0 fw-semibold">Módulo habilitado</p>
                <small className="text-muted">
                  Activa la integración para todos los roles autorizados.
                  El superadmin siempre tiene acceso.
                </small>
              </div>
              <div className="form-check form-switch ms-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="switchHabilitado"
                  checked={habilitado}
                  onChange={e => { setHabilitado(e.target.checked); marcarDirty(); }}
                  style={{ width: 48, height: 24, cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* Facturación automática */}
            <div className="d-flex align-items-center justify-content-between py-2 border-bottom mb-3">
              <div>
                <p className="mb-0 fw-semibold">Facturación automática</p>
                <small className="text-muted">
                  Al confirmar un pago, emite automáticamente la factura electrónica en Contífico.
                  Si está desactivado, la factura se genera de forma manual.
                </small>
              </div>
              <div className="form-check form-switch ms-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="switchFacAuto"
                  checked={facAuto}
                  onChange={e => { setFacAuto(e.target.checked); marcarDirty(); }}
                  style={{ width: 48, height: 24, cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* Próximo número de factura — override manual */}
            <div className="py-2 border-bottom mb-3">
              <p className="fw-semibold mb-1">Próximo número de factura <span className="badge bg-secondary ms-1" style={{ fontSize: 10 }}>uso único</span></p>
              <small className="text-muted d-block mb-2">
                Úsalo solo si el sistema no puede detectar la secuencia automáticamente (p. ej., al cambiar de establecimiento).
                Ingresa el número completo siguiente: <code>001-001-000008847</code>.
                Se limpiará automáticamente tras emitir la primera factura.
              </small>
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: 260, fontFamily: 'monospace' }}
                placeholder="001-001-000008847"
                value={proximoNumero}
                onChange={e => { setProximoNumero(e.target.value); marcarDirty(); }}
              />
              {proximoNumero && (
                <small className="text-warning d-block mt-1">
                  <i className="bi bi-exclamation-triangle me-1" />
                  Override activo — la próxima factura usará este número.
                </small>
              )}
            </div>

            {/* Cuenta bancaria por defecto (fallback para transferencias/depósitos) */}
            <div className="py-2 border-bottom mb-3">
              <p className="fw-semibold mb-1">
                Cuenta bancaria por defecto
                <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>fallback</span>
              </p>
              <small className="text-muted d-block mb-2">
                El cajero selecciona la cuenta al momento del pago, entre las que estén <strong>activas</strong>
                en el <strong>Catálogo de Cuentas Bancarias</strong> más abajo. Este valor se usa solo si hay una
                única cuenta activa o si el selector no carga. Selecciona aquí o ingresa el ID manualmente.
              </small>
              {cuentasBancarias.length > 0 ? (
                <select
                  className="form-select"
                  style={{ maxWidth: 400 }}
                  value={cuentaBancariaId}
                  onChange={e => { setCuentaBancariaId(e.target.value); marcarDirty(); }}
                >
                  <option value="">— Sin cuenta por defecto —</option>
                  {cuentasBancarias.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre_banco ? `${c.nombre_banco} — ` : ''}{c.nombre} ({c.numero})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: 300, fontFamily: 'monospace' }}
                    placeholder="ID de cuenta bancaria en Contífico"
                    value={cuentaBancariaId}
                    onChange={e => { setCuentaBancariaId(e.target.value); marcarDirty(); }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={cargarCuentasBancarias}
                    disabled={cargandoCuentas}
                  >
                    {cargandoCuentas
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-arrow-repeat me-1" />Cargar</>}
                  </button>
                </div>
              )}
            </div>

            {/* Serie de factura — Establecimiento + Emisión */}
            <div className="py-2">
              <p className="fw-semibold mb-1">
                Punto de emisión del API
              </p>
              <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                <i className="bi bi-exclamation-triangle-fill me-2" />
                <strong>Crítico:</strong> En Contífico, cada <em>API Token</em> pertenece a un único punto de emisión.
                Si usas el token del POS físico (ej. <code>001-002</code>) en lugar del token del API (<code>001-001</code>),
                Ademy generará facturas con la secuencia equivocada y colisionará con el POS físico.
                <br />
                Verifica en <strong>Contífico → Configuración → Puntos de Venta</strong> cuál tiene el tipo <strong>API</strong>
                y copia su Establecimiento y Emisión exactos aquí.
              </div>
              <small className="text-muted d-block mb-2">
                Corresponde a la fila del POS de Contífico configurada como <strong>API</strong>.
                Evita que Ademy use la secuencia del POS físico.
              </small>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div>
                  <label className="form-label mb-1 small text-muted">Establecimiento</label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: 90, fontFamily: 'monospace', textAlign: 'center' }}
                    placeholder="001"
                    value={establecimiento}
                    onChange={e => { setEstablecimiento(e.target.value.replace(/\D/g, '').slice(0, 3)); marcarDirty(); }}
                    maxLength={3}
                  />
                </div>
                <span className="fw-bold mt-3">-</span>
                <div>
                  <label className="form-label mb-1 small text-muted">Emisión</label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: 90, fontFamily: 'monospace', textAlign: 'center' }}
                    placeholder="001"
                    value={emision}
                    onChange={e => { setEmision(e.target.value.replace(/\D/g, '').slice(0, 3)); marcarDirty(); }}
                    maxLength={3}
                  />
                </div>
                {establecimiento && emision && (
                  <div className="mt-3">
                    <span className="badge bg-light border text-dark" style={{ fontFamily: 'monospace', fontSize: 13 }}>
                      Serie: {establecimiento}-{emision}
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Jobs automáticos */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-bottom py-3">
            <h6 className="mb-0 fw-semibold">
              <i className="bi bi-gear-wide-connected me-2 text-primary" />
              Jobs automáticos
            </h6>
            <small className="text-muted">
              Los cambios aplican en caliente al guardar — sin reiniciar el servidor.
            </small>
          </div>
          <div className="card-body">

            {/* Reintento de facturas fallidas */}
            <div className="border rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <p className="mb-0 fw-semibold">Reintento de facturas fallidas</p>
                  <small className="text-muted">
                    Reintenta automáticamente las facturas que no pudieron enviarse a Contífico.
                  </small>
                </div>
                <div className="form-check form-switch ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="switchReintento"
                    checked={reintentoHabilitado}
                    onChange={e => { setReintentoHabilitado(e.target.checked); marcarDirty(); }}
                    style={{ width: 48, height: 24, cursor: 'pointer' }}
                  />
                </div>
              </div>
              <div className={`row g-3 ${!reintentoHabilitado ? 'opacity-50 pe-none' : ''}`}>
                <div className="col-sm-4">
                  <label className="form-label small text-muted mb-1">Intervalo (min)</label>
                  <input
                    type="number" min={1} max={60}
                    className="form-control form-control-sm"
                    value={reintentoIntervalo}
                    onChange={e => { setReintentoIntervalo(e.target.value); marcarDirty(); }}
                  />
                  <small className="text-muted" style={{ fontSize: 11 }}>Cada cuántos minutos revisar</small>
                </div>
                <div className="col-sm-4">
                  <label className="form-label small text-muted mb-1">Máx. intentos</label>
                  <input
                    type="number" min={1} max={10}
                    className="form-control form-control-sm"
                    value={reintentoMaxIntentos}
                    onChange={e => { setReintentoMaxIntentos(e.target.value); marcarDirty(); }}
                  />
                  <small className="text-muted" style={{ fontSize: 11 }}>Antes de marcar rechazada</small>
                </div>
                <div className="col-sm-4">
                  <label className="form-label small text-muted mb-1">Edad mínima (min)</label>
                  <input
                    type="number" min={1} max={60}
                    className="form-control form-control-sm"
                    value={reintentoEdadMin}
                    onChange={e => { setReintentoEdadMin(e.target.value); marcarDirty(); }}
                  />
                  <small className="text-muted" style={{ fontSize: 11 }}>Esperar antes del 1er reintento</small>
                </div>
              </div>
            </div>

            {/* Polling de autorización SRI */}
            <div className="border rounded-3 p-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <p className="mb-0 fw-semibold">Polling de autorización SRI</p>
                  <small className="text-muted">
                    Consulta el estado de las facturas enviadas a Contífico hasta que el SRI las autorice.
                  </small>
                </div>
                <div className="form-check form-switch ms-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="switchPolling"
                    checked={pollingHabilitado}
                    onChange={e => { setPollingHabilitado(e.target.checked); marcarDirty(); }}
                    style={{ width: 48, height: 24, cursor: 'pointer' }}
                  />
                </div>
              </div>
              <div className={`row g-3 ${!pollingHabilitado ? 'opacity-50 pe-none' : ''}`}>
                <div className="col-sm-6">
                  <label className="form-label small text-muted mb-1">Intervalo (min)</label>
                  <input
                    type="number" min={1} max={60}
                    className="form-control form-control-sm"
                    value={pollingIntervalo}
                    onChange={e => { setPollingIntervalo(e.target.value); marcarDirty(); }}
                  />
                  <small className="text-muted" style={{ fontSize: 11 }}>Cada cuántos minutos consultar a Contífico</small>
                </div>
                <div className="col-sm-6">
                  <label className="form-label small text-muted mb-1">Alertar tras (horas)</label>
                  <input
                    type="number" min={1} max={72}
                    className="form-control form-control-sm"
                    value={pollingAlertaHoras}
                    onChange={e => { setPollingAlertaHoras(e.target.value); marcarDirty(); }}
                  />
                  <small className="text-muted" style={{ fontSize: 11 }}>Si una FAC lleva X horas sin autorizarse</small>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* Catálogo de productos Contífico */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0 fw-semibold">Catálogo de Productos Contífico</h6>
            <small className="text-muted">
              {productos.length > 0
                ? `${productos.length} productos en caché local. Sincroniza para actualizar.`
                : 'Sin catálogo. Sincroniza para importar todos los productos de Contífico.'}
            </small>
          </div>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleSyncCatalogo}
            disabled={syncandoCatalogo}
          >
            {syncandoCatalogo
              ? <><span className="spinner-border spinner-border-sm me-1" />Sincronizando...</>
              : <><i className="bi bi-arrow-repeat me-1" />Sincronizar catálogo</>}
          </button>
        </div>
        {productos.length > 0 && (
          <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ width: 120 }}>Código</th>
                  <th>Nombre</th>
                  <th style={{ width: 60 }} className="text-center">IVA</th>
                  <th style={{ width: 90 }} className="text-center">Visible</th>
                </tr>
              </thead>
              <tbody>
                {productos.map(p => (
                  <tr key={p.id} className={!p.activo ? 'opacity-50' : ''}>
                    <td><code className="small">{p.codigo}</code></td>
                    <td className="small">{p.nombre}</td>
                    <td className="text-center small">{p.iva}%</td>
                    <td className="text-center">
                      <div className="form-check form-switch d-flex justify-content-center mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!p.activo}
                          disabled={!!toggling[p.id]}
                          onChange={() => handleToggleProducto(p)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Catálogo de Cuentas Bancarias — decide qué cuentas ve el cajero al cobrar */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0 fw-semibold">Catálogo de Cuentas Bancarias</h6>
            <small className="text-muted">
              {cuentasCatalogo.length > 0
                ? `${cuentasCatalogo.length} cuentas en caché local. Solo las "Activas" aparecen en el pago del cajero.`
                : 'Sin catálogo. Sincroniza para importar las cuentas bancarias registradas en Contífico.'}
            </small>
          </div>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleSyncCuentas}
            disabled={syncandoCuentas}
          >
            {syncandoCuentas
              ? <><span className="spinner-border spinner-border-sm me-1" />Sincronizando...</>
              : <><i className="bi bi-arrow-repeat me-1" />Sincronizar cuentas</>}
          </button>
        </div>
        {cuentasCatalogo.length > 0 && (
          <div className="table-responsive" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                <tr>
                  <th>Banco</th>
                  <th>Cuenta</th>
                  <th style={{ width: 140 }}>Número</th>
                  <th style={{ width: 90 }} className="text-center">Activa</th>
                </tr>
              </thead>
              <tbody>
                {cuentasCatalogo.map(c => (
                  <tr key={c.id} className={!c.activo ? 'opacity-50' : ''}>
                    <td className="small">{c.nombre_banco || <span className="text-muted">—</span>}</td>
                    <td className="small">{c.nombre}</td>
                    <td><code className="small">{c.numero || '—'}</code></td>
                    <td className="text-center">
                      <div className="form-check form-switch d-flex justify-content-center mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={!!c.activo}
                          disabled={!!togglingCuenta[c.id]}
                          onChange={() => handleToggleCuenta(c)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bodegas — enlace manual con Contifico. Cada bodega local (una por
          centro) se enlaza a mano con su bodega real en Contifico, en vez de
          que el sync adivine por nombre exacto y termine creando una
          duplicada si el nombre no calza letra por letra. */}
      {bodegasLocales.length > 0 && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-0 fw-semibold">Bodegas → Bodegas Contífico</h6>
              <small className="text-muted">
                Elige a cual bodega real de Contífico corresponde cada bodega local, para que la
                sincronización de movimientos de inventario nunca tenga que adivinar por nombre.
              </small>
            </div>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={cargarBodegasContifico}
              disabled={cargandoBodegasContifico}
            >
              {cargandoBodegasContifico
                ? <span className="spinner-border spinner-border-sm" />
                : <><i className="bi bi-arrow-repeat me-1" />Actualizar lista</>}
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Bodega local</th>
                  <th style={{ width: 260 }}>Bodega en Contífico</th>
                  <th style={{ width: 90 }} className="text-center">Enlazada</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {bodegasLocales.map(b => (
                  <tr key={b.id}>
                    <td>
                      <span className="fw-semibold">{b.nombre}</span>
                      <small className="text-muted d-block">{b.centro_nombre}</small>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={bodegasEdit[b.id] ?? ''}
                        onChange={e => setBodegasEdit(prev => ({ ...prev, [b.id]: e.target.value }))}
                        disabled={cargandoBodegasContifico}
                      >
                        <option value="">— Sin enlazar —</option>
                        {bodegasContifico.map(bc => (
                          <option key={bc.id} value={bc.id}>{bc.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-center">
                      {b.contifico_bodega_id
                        ? <span className="badge bg-success"><i className="bi bi-check-lg" /></span>
                        : <span className="badge bg-light text-secondary border">—</span>}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary w-100"
                        onClick={() => handleGuardarBodega(b)}
                        disabled={!!guardandoBodega[b.id]}
                      >
                        {guardandoBodega[b.id]
                          ? <span className="spinner-border spinner-border-sm" />
                          : <i className="bi bi-floppy" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Conceptos de pago — mapeo a productos Contífico */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-bottom py-3">
          <h6 className="mb-0 fw-semibold">Conceptos de Pago → Productos Contífico</h6>
          <small className="text-muted">
            Solo para servicios extras (DNA, duplicados, etc.). Los conceptos de <strong>matrícula</strong> se configuran por curso o por matrícula individual.
          </small>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Concepto de pago</th>
                <th style={{ width: 220 }}>Producto Contífico</th>
                <th style={{ width: 80 }} className="text-center">Vinculado</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {conceptos.length === 0 && (
                <tr><td colSpan={4} className="text-center text-muted py-3">Cargando conceptos...</td></tr>
              )}
              {conceptos.map(c => (
                <tr key={c.id} className={!c.activo ? 'opacity-50' : ''}>
                  <td>
                    <span className="fw-semibold">{c.nombre}</span>
                    {c.afecta_matricula
                      ? <span className="badge bg-light text-secondary border ms-2" style={{ fontSize: 10 }}>usar matrícula</span>
                      : null}
                    {!c.activo ? <span className="badge bg-secondary ms-1" style={{ fontSize: 10 }}>inactivo</span> : null}
                  </td>
                  <td>
                    {c.afecta_matricula
                      ? <small className="text-muted fst-italic">Se define en la matrícula</small>
                      : (
                        <select
                          className="form-select form-select-sm"
                          value={codigosEdit[c.id] ?? ''}
                          onChange={e => setCodigosEdit(prev => ({ ...prev, [c.id]: e.target.value }))}
                          disabled={productos.length === 0}
                        >
                          <option value="">— Sin asignar —</option>
                          {productos.filter(p => p.activo).map(p => (
                            <option key={p.id} value={p.codigo}>{p.codigo} — {p.nombre}</option>
                          ))}
                        </select>
                      )}
                  </td>
                  <td className="text-center">
                    {c.contifico_producto_id
                      ? <span className="badge bg-success"><i className="bi bi-check-lg" /></span>
                      : <span className="badge bg-light text-secondary border">—</span>}
                  </td>
                  <td>
                    {!c.afecta_matricula && (
                      <button
                        className="btn btn-sm btn-outline-primary w-100"
                        onClick={() => handleGuardarConcepto(c)}
                        disabled={!!guardandoConcep[c.id] || productos.length === 0}
                      >
                        {guardandoConcep[c.id]
                          ? <span className="spinner-border spinner-border-sm" />
                          : <i className="bi bi-floppy" />}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Acciones */}
      <div className="d-flex gap-2 justify-content-end">
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handlePing}
          disabled={cargandoPing || guardando}
        >
          {cargandoPing
            ? <><span className="spinner-border spinner-border-sm me-1" />Verificando...</>
            : <><i className="bi bi-wifi me-1" />Probar conexión</>}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleGuardar}
          disabled={guardando || cargandoPing}
        >
          {guardando
            ? <><span className="spinner-border spinner-border-sm me-1" />Guardando...</>
            : <><i className="bi bi-floppy me-1" />Guardar configuración</>}
        </button>
      </div>

      {/* Barra flotante "cambios sin guardar" — evita depender de scrollear hasta el final
          y deja claro que togglear una opción (ej. facturación automática) NO se guarda solo. */}
      {dirty && (
        <div
          className="d-flex align-items-center gap-3 shadow-lg"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1045,
            background: '#fff',
            border: '1px solid #ffc107',
            borderRadius: 12,
            padding: '0.75rem 1.25rem',
            minWidth: 340,
          }}
        >
          <i className="bi bi-exclamation-circle-fill text-warning fs-5" />
          <span className="fw-semibold small">Tienes cambios sin guardar</span>
          <button
            type="button"
            className="btn btn-primary btn-sm ms-auto"
            onClick={handleGuardar}
            disabled={guardando}
          >
            {guardando
              ? <><span className="spinner-border spinner-border-sm me-1" />Guardando...</>
              : <><i className="bi bi-floppy me-1" />Guardar ahora</>}
          </button>
        </div>
      )}

    </div>
  );
}
